import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { eventTypeColor } from "components/scrapEyeDataDashboard/VideoPlayer/utils";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { MaterialRead } from "src/store/api/generatedApi";
import { MaterialLabel, ScrapEyesEventEdit } from "./ScrapEyesEventEdit";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { AreaLabel } from "components/scrapEyeLabelling/components/AreaLabel";
import { useFormatNumber } from "src/utils/formatNumber";
import { parseFiniteFloat } from "./ScaleWeightsChart.utils";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { isMassEventType } from "components/manualReview/utils/massEventWindow";

dayjs.extend(utc);

export const ScrapEyesEventCard = ({
  event,
  selectedEventId,
  recordingEventId,
  onSelectEvent,
  onDeleteEvent,
  onUpdateEvent,
  materialOptions,
  mappedMaterial,
  getAreaMaterialMappingAtTime,
  location,
}: {
  event: ScrapEyesEvent;
  selectedEventId: string | null;
  recordingEventId: string | null;
  onSelectEvent: (id: string | null) => void;
  onDeleteEvent?: (id: string) => void;
  onUpdateEvent?: (
    id: string,
    updates: {
      comment?: string;
      material: string | null;
      area?: string | null;
      mass?: string | null;
    }
  ) => void;
  materialOptions: MaterialRead[];
  mappedMaterial?: string | null;
  getAreaMaterialMappingAtTime?: (timestamp: Dayjs) => Record<string, string>;
  location: string;
}) => {
  const { t } = useTenantTranslation();
  const { mass_event_matching: massEventMatching } = useScrapEyeMetadata();
  const isSelected = event.id === selectedEventId;
  const isRecording = event.id === recordingEventId;
  const formatUnits = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const massValue = parseFiniteFloat(event.mass);
  const showMassChip = isMassEventType(event.eventType, massEventMatching);

  const hasMappingMismatch =
    event.eventType === "lift" &&
    event.material != null &&
    mappedMaterial != null &&
    event.material !== mappedMaterial &&
    mappedMaterial !== "unknown";

  return (
    <Paper
      data-testid="event-card"
      data-event-id={event.id}
      key={event.id}
      elevation={isSelected ? 4 : 1}
      sx={{
        p: 2,
        mb: 2,
        cursor: "pointer",
        border: (theme) =>
          isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : isRecording
              ? `2px solid ${theme.palette.error.main}`
              : "none",
        backgroundColor: (theme) =>
          isSelected
            ? theme.palette.action.selected
            : isRecording
              ? theme.palette.error.light + "20"
              : "background.paper",
        "&:hover": {
          elevation: 2,
          backgroundColor: (theme) => theme.palette.action.hover,
        },
      }}
      onClick={() => {
        onSelectEvent(event.id);
      }}
    >
      <Stack spacing={2}>
        {/* Header with title and delete button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              data-testid="event-type-label"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: eventTypeColor(event.eventType),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                {event.eventType.toUpperCase()}
              </Typography>
            </Box>
            <AreaLabel area={event.area} label={event.areaLabel} />

            {isRecording ? (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "red",
                  "@keyframes blink": {
                    "0%, 100%": {
                      opacity: 1,
                    },
                    "50%": {
                      opacity: 0,
                    },
                  },
                  animation: "blink 1s infinite",
                }}
              />
            ) : null}
          </Stack>
          {onDeleteEvent ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteEvent(event.id);
              }}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          {event.eventType !== "flag" ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <MaterialLabel
                material={
                  materialOptions.find(
                    (material) => material.name === event.material
                  ) ?? null
                }
              />
              {hasMappingMismatch ? (
                <Tooltip title={t("materialMismatchWithMapping")} arrow>
                  <SyncProblemIcon
                    sx={{ fontSize: 16, color: "warning.main" }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          ) : null}
          {showMassChip ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {event.warnings && event.warnings.length > 0 ? (
                <Tooltip
                  title={event.warnings.map((w) => t(w)).join(", ")}
                  arrow
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 16, color: "warning.main" }}
                  />
                </Tooltip>
              ) : null}
              {massValue !== null && massValue !== 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    backgroundColor: "grey.200",
                    fontWeight: "medium",
                  }}
                >
                  {formatNumber(2)(massValue)} {formatUnits("mass")}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {onUpdateEvent ? (
            <Box alignSelf="flex-start">
              <ScrapEyesEventEdit
                event={event}
                materialOptions={materialOptions}
                onUpdate={(updates) => {
                  onUpdateEvent(event.id, updates);
                }}
                getAreaMaterialMappingAtTime={getAreaMaterialMappingAtTime}
                location={location}
              />
            </Box>
          ) : null}
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="nowrap"
            fontSize={8}
          >
            {event.start.utc().format("HH:mm:ss")} -{" "}
            {event.end.utc().format("HH:mm:ss")} UTC
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};
